import { useState, useEffect, useRef } from 'react'

interface UseNewItemsDetectionOptions<T> {
  items: T[] | undefined
  enabled: boolean
  getItemId: (item: T) => string
  resetDependency?: string // When this changes, reset the detection
}

export function useNewItemsDetection<T>({
  items,
  enabled,
  getItemId,
  resetDependency,
}: UseNewItemsDetectionOptions<T>) {
  const [newItemsCount, setNewItemsCount] = useState(0)
  const previousItemIds = useRef<Set<string>>(new Set())
  const lastResetDependency = useRef<string | undefined>(resetDependency)

  useEffect(() => {
    // Reset detection when resetDependency changes
    if (resetDependency !== lastResetDependency.current) {
      previousItemIds.current = new Set()
      setNewItemsCount(0)
      lastResetDependency.current = resetDependency
      return
    }

    if (!items || !enabled) return

    const currentIds = new Set(items.map(getItemId))

    if (previousItemIds.current.size === 0) {
      previousItemIds.current = currentIds
      return
    }

    const newItems = items.filter((item) => !previousItemIds.current.has(getItemId(item)))

    if (newItems.length > 0) {
      setNewItemsCount((prev) => prev + newItems.length)
    }

    previousItemIds.current = currentIds
  }, [items, enabled, getItemId, resetDependency])

  const dismiss = () => {
    setNewItemsCount(0)
  }

  return {
    hasNewItems: newItemsCount > 0,
    newItemsCount,
    dismiss,
  }
}

